import React from 'react'
import { createPortal } from 'react-dom'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/Buttons/Button'
import { useTranslation } from '@/localization/useTranslation'
import { AppRedirectContent } from '../contents/AppRedirectContent'
import type { IModalScript, IModalAction } from '@/cinematics/modals/types'
import type { IGlobalProps } from '@/types/cinematics'
import { navigateTo } from '@/utils/url'
import classes from './styles.module.scss'

interface IConfigurableModalProps {
  modal: IModalScript
  stores?: IGlobalProps['stores']
  onClose: () => void
}

/** Generic modal shell: title + description + variant-specific content and
 *  actions. Kept dumb — visibility is owned by the trigger hook. */
export const ConfigurableModal: React.FC<IConfigurableModalProps> = ({ modal, stores, onClose }) => {
  const { t } = useTranslation()
  const { content } = modal

  const hasActions = !!content.actions?.length

  const runAction = (action: IModalAction['action']) => {
    // A valid link navigates; a rejected/blocked one falls through to dismiss so
    // the button is never an inert dead-end that traps the user in the modal.
    if (action.type === 'link' && action.url && navigateTo(action.url)) return
    onClose()
  }

  // Global modals mount deep inside the Layout tree, whose ancestors create a
  // containing block (transform/animation) that confines the fixed overlay and
  // caps its stacking level below confetti. Portal to <body> and wrap in a
  // stacking context above confetti so the modal is a true, top-most overlay.
  if (typeof document === 'undefined') return null

  return createPortal(
    <div className={classes.portal}>
      <Modal isVisible size="medium" onClose={onClose} placement={modal.placement ?? 'top'} closeIcon="close">
        <Modal.Content>
          <Modal.Title>{t(content.title)}</Modal.Title>
          <Modal.Description>{t(content.description)}</Modal.Description>
          {content.variant === 'app-redirect' &&
            <AppRedirectContent storeKeys={content.stores} stores={stores} />
          }
        </Modal.Content>

        {hasActions
          ? (
            <Modal.Actions>
              {content.actions?.map((item, index) => (
                <Button
                  key={index}
                  variant={item.action.variant}
                  onClick={() => runAction(item.action)}
                >
                  {t(item.text)}
                </Button>
              ))}
            </Modal.Actions>
          )
          // No configured actions: `default` needs a dismiss button (no other
          // CTA); `app-redirect` already has store badges + the ✕ close.
          : content.variant === 'default' && (
            <Modal.Actions>
              <Button variant="primary" onClick={onClose}>{t('Accept')}</Button>
            </Modal.Actions>
          )
        }
      </Modal>
    </div>,
    document.body
  )
}
